import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CCard, CCardBody, CButton } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { FormSetupContext } from '../FormSetup';
import StepCardHeader from '../components/StepCardHeaders';
import CLabel from '../../../../../migration/CLabel';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import { callTokenApi } from '../../../../../../apiCaller';
import { API_ADMIN_ACCOUNT_ENRICHMENT_RULES } from '../../../../../../constants';
import { useAdminTriggersNameAccount } from '../../../../../../helpers/customHooks';
import { toastError } from '../../../../../../utils';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { CSelect } from '../../../../../migration/CInput';
import { ConfirmSaveChange } from '../../../../../general/popup';
import { toast } from 'react-toastify';
import CConditionOptions from '../components/CConditionOptions';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';

const eventTypes = ['Event', 'Conversion', 'Trigger'];

const Conditions = ({ enrichmentType: accountEnrichmentType, setAccountEnrichment }) => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        setEnrichmentList,
        initStepsData,
        setInitStepsData,
        accountId,
        isPixelRule,
    } = useContext(FormSetupContext);

    const { ruleType } = stepsData; 

    const initialGroup = {
        type: '',
        key: '',
        operator: 'eq',
        value: 'true',
        conjunction: '',
        ...(!isPixelRule && { enrichmentType: '' }),
    };

    const enrichmentType = !isPixelRule && ruleType === 'Enrichment Rule' ? accountEnrichmentType : [];

    const [saveModal, setSaveModal] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [errors, setErrors] = useState([]);
    const groupKeysRef = React.useRef([]);

    const { triggersName, conversionsName, isLoading } = useAdminTriggersNameAccount();
    const ID_AND_KEY_PROP_NAME = 'id & key';

    useEffect(() => {
        if (activeStep === 2) {
            const conditionGroups = cloneDeep(stepsData.conditions);
            groupKeysRef.current = conditionGroups.map((_, i) => groupKeysRef.current[i] || `group-${Date.now()}_${i}`);
            setGroups(conditionGroups);
        }
    }, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSaveModal = useCallback(() => {
        setSaveModal(!saveModal);
    }, [saveModal]);

    const changeAValueOfGroup = ({ lvl1GroupIndex, lvl2GroupIndex, propName, propValue }) => {
        let newGroups = [...groups];

        if (propName === 'type') {
            newGroups[lvl1GroupIndex][`g${lvl2GroupIndex}`] = {
                ...initialGroup,
                type: propValue,
                conjunction: newGroups[lvl1GroupIndex][`g${lvl2GroupIndex}`].conjunction,
                operator: eventTypes.includes(propValue) ? 'eq' : '',
                value: eventTypes.includes(propValue) ? 'true' : ''
            };
        } else if (propName === ID_AND_KEY_PROP_NAME) {
            let idAndKeyObj = JSON.parse(propValue);

            Object.entries(idAndKeyObj).forEach(([key, value]) => {
                newGroups[lvl1GroupIndex][`g${lvl2GroupIndex}`][key] = value;
            });
        } else if (['true', 'false'].includes(propValue)) {
            newGroups[lvl1GroupIndex][`g${lvl2GroupIndex}`].operator = 'eq';
            newGroups[lvl1GroupIndex][`g${lvl2GroupIndex}`].value = propValue;
        } else {
            newGroups[lvl1GroupIndex][`g${lvl2GroupIndex}`][propName] = propValue;
        }

        let newErrors = [...errors];
        if (newErrors[lvl1GroupIndex]) {
            delete newErrors[lvl1GroupIndex].group;
            if (newErrors[lvl1GroupIndex][`g${lvl2GroupIndex}`]) {
                delete newErrors[lvl1GroupIndex][`g${lvl2GroupIndex}`].row;
                delete newErrors[lvl1GroupIndex][`g${lvl2GroupIndex}`][propName];
                if (propName === 'type') {
                    delete newErrors[lvl1GroupIndex][`g${lvl2GroupIndex}`];
                } else if (propName === ID_AND_KEY_PROP_NAME || propName === 'id') {
                    delete newErrors[lvl1GroupIndex][`g${lvl2GroupIndex}`].key;
                }
            }
        }

        setErrors(newErrors);
        setGroups(newGroups);
    };

    const deleteLvl2Group = (lvl1GroupIndex, lvl2GroupIndex) => {
        let newGroups = [...groups];
        let newGroup = { ...newGroups[lvl1GroupIndex] };
        let newGroupConjunction = newGroup.conjunction;
        delete newGroup.conjunction;
        const newGroupLength = Object.keys(newGroup).length;

        // Remove the last group (last group's conjunction is empty), then set the previous group's conjunction to empty
        if (!newGroup[`g${lvl2GroupIndex}`].conjunction && lvl2GroupIndex !== 0) {
            newGroup[`g${lvl2GroupIndex - 1}`].conjunction = '';
        }

        for (let i = lvl2GroupIndex; i < newGroupLength; i++) {
            newGroup[`g${i}`] = { ...newGroup[`g${i + 1}`] };
        }

        delete newGroup[`g${newGroupLength - 1}`];
        newGroup.conjunction = newGroupConjunction;
        newGroups[lvl1GroupIndex] = { ...newGroup };

        setGroups(newGroups);
        setErrors([]);
    };

    const validate = useCallback(() => {
        // check require field
        let validate = true;
        let newErrors = [];

        for (let index = 0; index < groups.length; index++) {
            const lvl1Group = groups[index];
            let workingLvl1Group = { ...lvl1Group };
            delete workingLvl1Group.conjunction;
            newErrors[index] = {};

            for (const lvl2GroupKey in workingLvl1Group) {
                const { type, key, operator, value, enrichmentType } = workingLvl1Group[lvl2GroupKey];
                let lv2Errors = {};

                if (['exav', 'nex', 'itp', 'itft'].includes(operator)) {
                    if (!key) {
                        validate = false;
                    }
                } else if (!key || !operator || ((!isPixelRule && ruleType === 'Enrichment Rule') && !enrichmentType) || (typeof value === 'string' && !value.trim())) {
                    validate = false;
                }

                if (!validate) {
                    if (!type) {
                        lv2Errors = { ...lv2Errors, type: 'This field is required.' };
                    }
                    if (!key) {
                        lv2Errors = { ...lv2Errors, key: 'This field is required.' };
                    }
                    if (!operator) {
                        lv2Errors = { ...lv2Errors, operator: 'This field is required.' };
                    }
                    if (!value) {
                        lv2Errors = { ...lv2Errors, value: 'This field is required.' };
                    }

                    if (!isPixelRule && ruleType === 'Enrichment Rule') {
                        if (!enrichmentType) {
                            lv2Errors = { ...lv2Errors, enrichmentType: 'This field is required.' };
                        }
                    }
                }
                newErrors[index][lvl2GroupKey] = lv2Errors;
            }
        }

        // check validate dupplicate groups
        let duplicateGroups = false;
        let groupsToValidate = groups.map((group) => {
            let newGroup = { ...group };
            delete newGroup.conjunction;

            return newGroup;
        });

        for (let i = 0; i < groupsToValidate.length - 1; i++) {
            for (let j = i + 1; j < groupsToValidate.length; j++) {
                if (isEqual(groupsToValidate[i], groupsToValidate[j])) {
                    newErrors[j] = { group: 'There are duplicate group' };
                    duplicateGroups = true;
                    validate = false;
                    break;
                }
            }

            if (duplicateGroups) {
                break;
            }
        }

        if (!duplicateGroups) {
            for (let index = 0; index < groupsToValidate.length; index++) {
                const lvl1Group = groupsToValidate[index];
                const lvl1GroupLength = Object.keys(lvl1Group).length;

                for (let i = 0; i < lvl1GroupLength; i++) {
                    let iGroup = { ...lvl1Group[`g${i}`] };
                    iGroup.conjunction = '';

                    for (let j = i + 1; j < lvl1GroupLength; j++) {
                        let jGroup = { ...lvl1Group[`g${j}`] };
                        jGroup.conjunction = '';

                        if (isEqual(iGroup, jGroup)) {
                            if (!newErrors[index]) {
                                newErrors[index] = {};
                            }
                            if (!newErrors[index][`g${j}`] || Object.keys(newErrors[index][`g${j}`]).length === 0) {
                                newErrors[index][`g${j}`] = { row: "There are duplicate row's values in the same group" };
                            }
                            duplicateGroups = true;
                        }
                    }
                }

                if (duplicateGroups) {
                    validate = false;
                }
            }
        } else {
            validate = false;
        }

        setErrors(newErrors);
        return validate;
    }, [groups, isPixelRule, ruleType]);

    const handleSave = () => {
        if (validate()) {
            const newValues = {
                ...stepsData,
                conditions: groups,
            }

            if (stepsData.id && !isEqual(initStepsData, newValues)) {
                setStepsData(newValues);
                toggleSaveModal();
            } else {
                setStepsData(newValues);
                setActiveStep(3);
            }
        }
    };

    const addLvl2Conjunction = ({ lvl1GroupIndex, lvl2GroupIndex, conjunction }) => {
        let newGroups = [...groups];

        if (!newGroups[lvl1GroupIndex][`g0`].conjunction) {
            newGroups[lvl1GroupIndex][`g0`].conjunction = 'or';
        }
        if (!newGroups[lvl1GroupIndex][`g${lvl2GroupIndex}`].conjunction) {
            newGroups[lvl1GroupIndex][`g${lvl2GroupIndex}`].conjunction = conjunction;
        }
        newGroups[lvl1GroupIndex][`g${lvl2GroupIndex + 1}`] = { ...initialGroup };

        setGroups(newGroups);
        setErrors([]);
    };

    const onSave = () => {
        const { name, description, conditions, type } = stepsData;
        const data = {
            name,
            description,
            accountId,
            conditions: JSON.stringify({ conditions }),
            ...(isPixelRule && { type }),
        };

        const pathURL = `${API_ADMIN_ACCOUNT_ENRICHMENT_RULES}/${stepsData.id}`;

        setSaveLoading(true);
        callTokenApi(pathURL, 'PUT', data)
            .then((response) => {
                if (response.status === 200 || response.status === 201) {
                    const responseData = response?.data?.data;

                    setAccountEnrichment((prev) => {
                        let updatedList;
                        const existsIndex = prev.accountEnrichmentList.findIndex(tpl => tpl.id === responseData.id);

                        if (existsIndex !== -1) {
                            updatedList = [...prev.accountEnrichmentList];
                            updatedList[existsIndex] = responseData;
                        } else {
                            updatedList = [responseData, ...prev.accountEnrichmentList];
                        }

                        return {
                            ...prev,
                            accountEnrichmentList: updatedList,
                        };
                    });

                    setEnrichmentList((prev) => {
                        const existsIndex = prev.findIndex(tpl => tpl.id === responseData.id);

                        if (existsIndex !== -1) {
                            const updatedTemplates = [...prev];
                            updatedTemplates[existsIndex] = responseData;
                            return updatedTemplates;
                        }

                        return [responseData, ...prev];
                    });
                    setInitStepsData(stepsData);
                    setActiveStep(2);

                    toast.success(`Rules ${stepsData?.id ? 'updated' : 'created'} successfully.`);
                } else {
                    toastError(response);
                }
            })
            .finally(() => {
                setSaveLoading(false);
                toggleSaveModal();
            });
    };

    return (
        <StepCardHeader
            title={`Conditions to Match`}
            step={2}
            activeStep={activeStep}
            lastEditStep={ruleType ? 1 : 0}
            setActiveStep={() => setActiveStep(2)}
            classBody='card-body-rules'
        >
            {isLoading ? (
                <CenterSpinner />
            ) : (
                groups.map((lvl1Group, lvl1GroupIndex) => {
                let workingLvl1Group = { ...lvl1Group };
                delete workingLvl1Group.conjunction;
                const groupDupplicate = errors[lvl1GroupIndex]?.group;

                return (
                    <React.Fragment key={groupKeysRef.current[lvl1GroupIndex]}>
                        <CCard className="conditions-validate">
                            <CCardBody className="list-rule-conversion">
                                <div className="d-flex flex-column gap-2">
                                    {Object.entries(workingLvl1Group).map(
                                        ([key, lvl2Group], lvl2GroupIndex) => {
                                            const lvl2GroupConjunction = lvl1Group[`g0`].conjunction || '';
                                            let activeTriggerOption, activeConversionOption;

                                            // id and key go together so checking one of them is enough
                                            if (lvl2Group.key) {
                                                switch (lvl2Group.type) {
                                                    case 'Conversion':
                                                        activeConversionOption = conversionsName.find((option) => JSON.parse(option.value).id === lvl2Group.id);
                                                        break;
                                                    case 'Trigger':
                                                        activeTriggerOption = triggersName.find((option) => JSON.parse(option.value).id === lvl2Group.id);
                                                        break;
                                                    default:
                                                }
                                            }

                                            const errorLvl2Group = errors[lvl1GroupIndex]?.[key] || {};
                                            const rowDupplicateValidate = errorLvl2Group.row;
                                            const isLastRow = key === `g${Object.entries(workingLvl1Group).length - 1}`;
                                            const isValidate = rowDupplicateValidate || (isLastRow && groupDupplicate);

                                            return (
                                                <div className='mb-1' key={key}>
                                                    <div className={`mb-2 conditions-match d-flex align-items-top${isValidate ? ' is-invalid' : ''}`}>
                                                        <div className="select-type">
                                                            <CLabel>Type</CLabel>
                                                            <CSelect
                                                                value={lvl2Group.type}
                                                                onChange={(e) => changeAValueOfGroup({
                                                                    lvl1GroupIndex,
                                                                    lvl2GroupIndex,
                                                                    propName: 'type',
                                                                    propValue: e.target.value,
                                                                })}
                                                                className={`${!lvl2Group.type ? 'not-selected' : ''}`}
                                                                invalid={Boolean(errorLvl2Group.type)}
                                                            >
                                                                <option value="" disabled hidden>Select</option>
                                                                <option value="Conversion">Conversion</option>
                                                                <option value="Trigger">Trigger</option>
                                                            </CSelect>

                                                            {errorLvl2Group.type && <CInvalidFeedback>{errorLvl2Group.type}</CInvalidFeedback>}
                                                        </div>

                                                        {(lvl2Group.type === 'Trigger' || lvl2Group.type === 'Conversion') && (
                                                            <CConditionOptions
                                                                selectType={lvl2Group.type}
                                                                selectOptions={lvl2Group.type === 'Trigger' ? triggersName : conversionsName}
                                                                activeOption={lvl2Group.type === 'Trigger' ? activeTriggerOption : activeConversionOption}
                                                                lvl1GroupIndex={lvl1GroupIndex}
                                                                lvl2GroupIndex={lvl2GroupIndex}
                                                                lvl2Group={lvl2Group}
                                                                changeAValueOfGroup={changeAValueOfGroup}
                                                                enrichmentType={enrichmentType}
                                                                error={errors[lvl1GroupIndex]?.[key]}
                                                            />
                                                        )}

                                                        <div className={` button-delete-rule `}>
                                                            {Object.keys(workingLvl1Group).length > 1 && (
                                                                <CIcon
                                                                    icon="iconDeleteField"
                                                                    className="icon-delete"
                                                                    onClick={() => deleteLvl2Group(lvl1GroupIndex, lvl2GroupIndex)}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {rowDupplicateValidate && <CInvalidFeedback>{rowDupplicateValidate}</CInvalidFeedback>}
                                                    {(isLastRow || groupDupplicate) && <CInvalidFeedback>{groupDupplicate}</CInvalidFeedback>}

                                                    {lvl2GroupIndex ===
                                                        Object.entries(workingLvl1Group).length - 1 && (
                                                            <CButton
                                                                className="btn-add-row mt-3"
                                                                onClick={() => addLvl2Conjunction({
                                                                    lvl1GroupIndex,
                                                                    lvl2GroupIndex,
                                                                    conjunction: lvl2GroupConjunction,
                                                                })}
                                                            >
                                                                <CIcon icon="iconAddField" className="icon-add" />
                                                                <CLabel className="add-row">ADD ROW</CLabel>
                                                            </CButton>
                                                        )}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            </CCardBody>
                        </CCard>
                    </React.Fragment>
                );
                })
            )}

            <CButton className="mt-2 px-4" color="primary" onClick={handleSave}>Save & next</CButton>

            <ConfirmSaveChange
                show={saveModal}
                onClose={() => setSaveModal(false)}
                isLoading={saveLoading}
                onAccept={onSave}
                title={`Are you sure you want to save?`}
            >
                <p>
                    You are about to save this new rule. After doing this, publish your changes to see your rule working in your data
                    layer.
                </p>
            </ConfirmSaveChange>
        </StepCardHeader>
    )
}

export default Conditions